<script setup lang="ts">
import BasicBlock from '@/components/shared/blocks/BasicBlock.vue';
import { DepartmentFragment } from '@/graphql/custom-types';
import { IDepartment } from '@/interfaces';
import DepartmentsOverlay from '@/overlays/DepartmentsOverlay.vue';
import { computed, ref } from 'vue';

const showDepartmentsOverlay = ref<boolean>(false);

interface Props {
  departments: (IDepartment | DepartmentFragment)[];
}

const props = withDefaults(defineProps<Props>(), {});

const MAX_CHARS = 40;

const departmentList = computed(() => {
  const names = props.departments.map((department) => department.name);
  const fullString = names.join(', ');

  if (fullString.length <= MAX_CHARS) return fullString;

  let length = 0;
  const visible = [];

  for (const name of names) {
    if (length + name.length > MAX_CHARS) break;
    visible.push(name);
    length += name.length;
  }

  const remaining = names.length - visible.length;
  if (remaining === 0) return fullString;
  return `${visible.join(', ')}, +${remaining}`;
});
</script>

<template>
  <div>
    <BasicBlock
      :icon="['fal', 'house']"
      :ariaDescription="$t('global.departments')"
      :text="departmentList"
      typography="b600-bold"
      canTriggerOverlay
      @click="showDepartmentsOverlay = true"
    >
    </BasicBlock>

    <DepartmentsOverlay
      :title="$t('global.departments')"
      :departments="departments"
      v-if="showDepartmentsOverlay"
      @close="showDepartmentsOverlay = false"
    />
  </div>
</template>
